import classNames from 'classnames/bind';
import { NextRouter } from 'next/router';
import React, { useState } from 'react';

import AirlineLogo from 'common/airlineLogo';
import Emissions, { getCarbonEmissionInfo } from 'common/emissions';
import { getStopCount } from 'common/utils';
import { SearchType } from 'contexts/searchParams';
import useExtraContents, {
  isAmenityAvailable,
} from 'hooks/useDetailExtraContents';
import Airlines from 'list/common/airline/airlines';
import Route from 'list/common/route';
import {
  IInternationalContextResult,
  IScheduleDetail,
  ScheduleKeys,
} from 'list/international/types';
import { toDays } from 'list/international/utils';

import ExtraPhoto from './extraPhoto';
import { AmenityAvailability } from './types';
import { areaCode, showFlightTime } from './utils';

import styles from './detailSchedule.module.scss';

const cx = classNames.bind(styles);

const defaultExtra = {
  equipmentDesc: '',
  inFlightEntertainment: '',
  InFlightMealImages: [],
  InsideImages: [],
  OutsideImages: [],
  mealsAndBeverages: '',
  seatArrangement: '',
  seatInfo: '',
  usb: '',
  wiFi: '',
};

interface DetailScheduleContentProps {
  flightResult: IInternationalContextResult;
  schedule: IScheduleDetail[];
  journeyTime: string[];
  title: string;
  searchType: SearchType;
  router: NextRouter;
  departureHighlight: boolean;
  arrivalHighlight: boolean;
  setIsPhotoClick: React.Dispatch<React.SetStateAction<boolean>>;
}

const DetailScheduleContent: React.FC<DetailScheduleContentProps> = ({
  flightResult,
  title,
  schedule,
  journeyTime,
  searchType,
  router,
  departureHighlight,
  arrivalHighlight,
  setIsPhotoClick,
}) => {
  const { airlines = {}, airports = {} } = flightResult;
  const [showDetail, setShowDetail] = useState(false);
  const { extraIcons, extraList } = useExtraContents(schedule, router);

  if (!schedule) return null;

  const [firstSchedule, lastSchedule] = [
    schedule[0],
    schedule[schedule.length - 1],
  ];
  const [propSdt, propEdt, firstSa, lastEa] = [
    toDays(firstSchedule[ScheduleKeys.startDateTime]),
    toDays(lastSchedule[ScheduleKeys.endDateTime]),
    firstSchedule[ScheduleKeys.startAirport],
    lastSchedule[ScheduleKeys.endAirport],
  ];
  const avs = Array.from(
    new Set(schedule.map(s => s[ScheduleKeys.airlineVendor])),
  );
  const oavs = schedule.reduce<Array<string>>(
    (acc, { oav }: IScheduleDetail) => {
      if (oav) {
        acc.push(oav);
      }
      return acc;
    },
    [],
  );
  const dayDiff = propEdt?.startOf('d').diff(propSdt?.startOf('d'), 'd');
  const stops = getStopCount(schedule);

  const { carbonEmission, carbonEmissionAverage } = getCarbonEmissionInfo({
    schedule,
    carbonEmissionAverages: flightResult.carbonEmissionAverage,
  });

  return (
    <>
      <div className={cx('summary')}>
        <div className={cx('row')}>
          <Airlines avs={avs} airlines={airlines} />
          {oavs && oavs.length > 0 && (
            <sub className={cx('oavs')}>
              {airlines[oavs[0]]
                ? `${airlines[oavs[0]]}${oavs.length > 1 ? ' 등' : ''} 공동운항`
                : '공동운항 정보확인필요'}
            </sub>
          )}
          <button
            type="button"
            className={cx('toggle')}
            aria-expanded={showDetail}
            data-event-nlog="click"
            data-event-area-code={`${areaCode}.morecontents`}
            onClick={() => {
              setShowDetail(!showDetail);
              setIsPhotoClick(false);
            }}
          >
            <span className={cx('txt')}>
              {extraIcons.MealsAndBeverages && (
                <em className={cx('item', 'as_2')} />
              )}
              {extraIcons.WiFi && <em className={cx('item', 'as_3')} />}
              {extraIcons.USB && <em className={cx('item', 'as_4')} />}
              {extraIcons.InFlightEntertainment && (
                <em className={cx('item', 'as_5')} />
              )}
              {Object.values(extraIcons).every(value => !value) &&
                '자세히 보기'}
            </span>
          </button>
        </div>
        <div className={cx('route')}>
          <Route
            departureTime={propSdt.format('HH:mm')}
            arrivalTime={propEdt.format('HH:mm')}
            departureCity={firstSa}
            arrivalCity={lastEa}
            dayDiff={dayDiff}
            stop={stops}
            searchType={searchType}
            journeyTime={journeyTime}
            schedule={schedule}
            departureHighlight={departureHighlight}
            arrivalHighlight={arrivalHighlight}
          />
        </div>
      </div>
      {showDetail && (
        <div className={cx('detail')}>
          {carbonEmissionAverage && (
            <div className={cx('emissions')}>
              <Emissions
                clickCodePrefix="idt"
                carbonEmission={carbonEmission}
                carbonEmissionAverage={carbonEmissionAverage}
              />
            </div>
          )}
          <div className={cx('selected_route')}>
            <b className={cx('title')}>{title.replace('항공편', '')}</b>

            {schedule.map((item, idx) => {
              const { sdt, edt, sa, av, fn, ea, oav, ha } = item;
              const {
                equipmentDesc,
                existContent,
                inFlightEntertainment,
                mealsAndBeverages,
                photo,
                seatArrangement,
                seatInfo,
                usb,
                wiFi,
              } = extraList[idx] || defaultExtra;
              const sdtDay = toDays(sdt);
              const edtDay = toDays(edt);

              return (
                <div key={`detail_${idx}`} className={cx('trip')}>
                  <div className={cx('route')}>
                    <b className={cx('name')}>{`${sa} ${
                      airports[sa]
                    } ${sdtDay.format('MM.DD. (dd) HH:mm')}`}</b>
                    <figure className={cx('logo')}>
                      <AirlineLogo
                        airlineCode={av}
                        alt={airlines[av]}
                        className={cx('img')}
                      />
                    </figure>
                    <i className={cx('info')}>
                      {av}
                      {fn}{' '}
                      {oav
                        ? airlines[oav]
                          ? `${airlines[oav]} 공동운항`
                          : '공동운항 정보확인필요'
                        : airlines[av]}
                      <br />
                      비행 {showFlightTime(item.ft)}
                    </i>

                    {existContent && (
                      <ul className={cx('list')}>
                        {equipmentDesc && (
                          <li className={cx('item', 'as_0')}>
                            {equipmentDesc}
                          </li>
                        )}
                        {seatArrangement && (
                          <li className={cx('item', 'as_1')}>
                            좌석배열 {seatArrangement}
                          </li>
                        )}
                        {isAmenityAvailable(mealsAndBeverages) && (
                          <li className={cx('item', 'as_2')}>
                            기내식
                            {mealsAndBeverages ===
                              AmenityAvailability.CHARGEABLE && ' 유료'}
                          </li>
                        )}
                        {isAmenityAvailable(wiFi) && (
                          <li className={cx('item', 'as_3')}>
                            와이파이
                            {wiFi === AmenityAvailability.CHARGEABLE && ' 유료'}
                          </li>
                        )}
                        {isAmenityAvailable(usb) && (
                          <li className={cx('item', 'as_4')}>
                            USB 전원
                            {mealsAndBeverages ===
                              AmenityAvailability.CHARGEABLE && ' 유료'}
                          </li>
                        )}
                        {isAmenityAvailable(inFlightEntertainment) && (
                          <li className={cx('item', 'as_5')}>
                            기내 엔터테인먼트
                            {inFlightEntertainment ===
                              AmenityAvailability.CHARGEABLE && ' 유료'}
                          </li>
                        )}
                        {seatInfo && (
                          <li className={cx('item', 'as_6')}>{seatInfo}</li>
                        )}
                      </ul>
                    )}
                    {photo && photo.length > 0 && (
                      <div className={cx('photo')}>
                        <ExtraPhoto
                          imageList={photo}
                          airline={equipmentDesc}
                          setIsPhotoClick={setIsPhotoClick}
                        />
                      </div>
                    )}
                  </div>

                  {/* 숨은 경유지 */}
                  {ha &&
                    ha.length > 0 &&
                    ha.reduce((res: JSX.Element[], { ap, st }, i: number) => {
                      if (!ap || !st) return res; // ap, st 가 '' 있을때가 있음
                      res.push(
                        <div className={cx('route')} key={i}>
                          <b
                            className={cx('name')}
                          >{`${ap} ${airports[ap]} 체류`}</b>
                          <figure className={cx('logo')}>
                            <AirlineLogo
                              airlineCode={av}
                              alt={airlines[av]}
                              className={cx('img')}
                            />
                          </figure>
                          <i className={cx('info')}>
                            {av}
                            {fn}{' '}
                            {oav
                              ? airlines[oav]
                                ? `${airlines[oav]} 공동운항`
                                : '공동운항 정보확인필요'
                              : airlines[av]}
                            <br />
                            {!!+st && `대기 ${showFlightTime(st)}`}
                          </i>
                        </div>,
                      );
                      return res;
                    }, [])}

                  <div className={cx('route')}>
                    <b className={cx('name')}>{`${ea} ${
                      airports[ea]
                    } ${edtDay.format('MM.DD. (dd) HH:mm')}`}</b>
                    {+item.ct > 0 && (
                      <i className={cx('info')}>
                        대기 {showFlightTime(item.ct)}
                      </i>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {extraList.some(
            (item: any) => item.existContent || item.photo.length > 0,
          ) && (
            <div className={cx('routes_info')}>
              <p className={cx('info')}>
                <span className={cx('txt')}>
                  항공기 부가서비스와 사진은 참고용이며 실제 탑승 상황 <wbr />
                  등에 따라 달라질 수 있습니다.
                </span>
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DetailScheduleContent;
